/* eslint-disable no-nested-ternary */
import PropTypes from 'prop-types';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Modal,
    Typography,
    TextField,
    InputAdornment,
    Chip,
    IconButton,
    Button,
    List,
    Tooltip,
    CircularProgress,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { m } from 'framer-motion';
import { useSelector, dispatch } from '../../redux/store';
import { getPracticeId } from '../../redux/slices/clincheck';
import { getSearchWpTemplateList, toggleTemplateFavourite } from '../../redux/slices/whatsAppMessages';
import { refreshWhatsappTemplatesForBusiness, syncTemplates } from '../../redux/slices/whatsappV2';
import { updateError } from '../../redux/slices/user';
import { getWhatsappAccount } from '../../redux/slices/whatsappAccount';
import { callApi } from '../../utils/apiCall';
import {
    getFirstConnectedWhatsappBusinessAccount,
    isConnectedWhatsappBusinessAccount,
} from '../../utils/whatsappAccountLifecycle';
import { APPLE, appleInputSx } from '../templates/appleDesignTokens';
import RequestTemplateDialog from '../templates/RequestTemplateDialog';
import WhatsAppTemplatePreviewPanel from './WhatsAppTemplatePreviewPanel';
import WhatsAppTemplateListItem, { normTemplateStatus } from './WhatsAppTemplateListItem';
import {
    filterNormalWhatsAppTemplates,
    isNormalWhatsAppTemplate,
} from './quickReplyUtils';
import { getPracticeTreatmentList } from '../../redux/slices/practiceTreatment';
import useAudiencePermission from './useAudiencePermission';
import { audienceSummary } from './audienceRulesUtils';
import {
    WA_GREEN,
    WA_LIGHT_GREEN,
    getUsageTypeColor,
    formatUsageType,
    formatTemplateName,
    getTemplateComponents,
    matchesSearch,
} from './whatsAppTemplateUtils';

// Why the Send button is not simply "Send Template". Extracted so the JSX stays
// flat and every not-sendable reason is visible in one place.
const sendButtonLabel = ({ audienceReason, audienceLoading, statusBucket }) => {
    if (audienceLoading) return 'Checking…';
    if (audienceReason) return 'Not sendable';
    if (statusBucket === 'PENDING') return 'Awaiting Approval';
    if (statusBucket === 'REJECTED') return 'Rejected';
    return 'Send Template';
};

const WhatsAppTemplateSelector = ({
    open,
    onClose,
    templates = [],
    onSend,
    onSelect,
    selectedTemplate: externalSelectedTemplate,
    loading = false,
    contextMapping = {},
    title = 'Select WhatsApp Template',
    onRequestTemplate,
    onEditTemplate,
    providerAccount,
    // When supplied, the selector opens pre-filtered to this status tab.
    // Accepted values: 'ALL' | 'APPROVED' | 'PENDING' | 'REJECTED'
    initialStatus = 'ALL',
    // Lead this send is for. When supplied, templates whose audience rules
    // forbid this patient (NHS / treatment whitelist or blacklist) are removed
    // from the list rather than offered and then refused at send time.
    // Omit it — campaign/gallery pickers with no single recipient — and no
    // audience filtering happens at all.
    leadId = null,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery('(max-width:768px)');
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

    const [searchText, setSearchText] = useState('');
    // null = no category filter (all templates shown); string = active filter
    const [selectedCategory, setSelectedCategory] = useState(null);
    // Status tab state — 'ALL' | 'APPROVED' | 'PENDING' | 'REJECTED'
    const [selectedStatus, setSelectedStatus] = useState('ALL');
    const [internalSelectedTemplate, setInternalSelectedTemplate] = useState(null);
    const [mobileShowList, setMobileShowList] = useState(false);
    const prevOpenRef = useRef(false);

    // ── Internal create/edit dialog state ──
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [favouriteFeedback, setFavouriteFeedback] = useState('');
    const [templateSubmitFeedback, setTemplateSubmitFeedback] = useState(null);
    const [pendingTemplateName, setPendingTemplateName] = useState('');
    // ── Sync state — for the Pending-tab "Check approval status" button ──
    const [syncing, setSyncing] = useState(false);
    const favouriteFeedbackTimerRef = useRef(null);

    // Redux — pull wpAccountId + practiceId for built-in create/edit
    const { whatsappAccountData } = useSelector((state) => state.whatsappAccount);
    const fallbackConnectedAccount = getFirstConnectedWhatsappBusinessAccount(whatsappAccountData);
    const selectedProviderAccount = providerAccount === undefined
        ? fallbackConnectedAccount
        : providerAccount;
    // Callers pass providerAccount built from practiceMetaData, whose
    // whatsAppBusiessAccountId is frequently undefined in the inbox. Falling
    // back to the connected account from redux keeps the audience lookup alive
    // — without this the eligibility fetch silently no-ops while Send stays
    // enabled, which is the worst possible combination.
    const wpAccountId = selectedProviderAccount?.id || fallbackConnectedAccount?.id;
    const canWriteProvider = isConnectedWhatsappBusinessAccount(selectedProviderAccount);
    const practiceId = getPracticeId();

    const normalTemplates = useMemo(
        () => filterNormalWhatsAppTemplates(templates),
        [templates]
    );
    const hasExternalSelection = externalSelectedTemplate !== undefined;
    const candidateSelectedTemplate = hasExternalSelection
        ? externalSelectedTemplate
        : internalSelectedTemplate;
    const findAvailableTemplate = useCallback((candidate) => {
        if (!isNormalWhatsAppTemplate(candidate)) return null;
        return normalTemplates.find((template) => (
            template === candidate
            || (candidate.id != null && template.id != null
                && String(template.id) === String(candidate.id))
        )) || null;
    }, [normalTemplates]);
    const selectedTemplate = findAvailableTemplate(candidateSelectedTemplate);

    // Compute existing template names for duplicate-name validation in create dialog
    const existingNames = useMemo(() => (templates || []).map(t => t.name), [templates]);

    // ── Audience eligibility for `leadId` ─────────────────────────────────
    // Fetched from the backend rather than recomputed here: the rules involve
    // the lead's treatments and the practice default, and a second
    // implementation on the client would be one more thing to keep in step.
    // Purely advisory — the send boundary re-decides and returns 409 if a
    // stale client tries anyway.
    const { canView: canViewAudience, canManage: canManageAudience } = useAudiencePermission();

    // Treatment catalogue for the audience picker inside the edit dialog.
    // Fetched only when the dialog is actually open AND the operator can edit
    // audience, so the selector costs nothing extra in the common send flow.
    const practiceTreatments = useSelector((state) => state.practiceTreatment?.treatmentsList?.results) || [];
    useEffect(() => {
        if (createDialogOpen && canManageAudience && practiceId) {
            dispatch(getPracticeTreatmentList(practiceId, { limit: 500, offset: 0 }));
        }
    }, [createDialogOpen, canManageAudience, practiceId]);

    // Persist audience straight to the template. Deliberately separate from
    // handleDialogSubmit, which submits template CONTENT to Meta.
    // `audienceMap`      id -> reason, for templates the backend REFUSED.
    // `audienceChecked`   id -> true, for every template the backend answered
    //                     about. Its absence is what tells us a restricted
    //                     template could not be verified, which must NOT read
    //                     as "sendable".
    const [audienceMap, setAudienceMap] = useState({});
    const [audienceChecked, setAudienceChecked] = useState({});
    // TRUE from the moment the picker opens with a lead until the eligibility
    // answer lands. Send stays disabled for that window: defaulting to
    // "sendable" while we do not yet know is a fail-OPEN, and the whole point
    // of the rule is that the operator never gets the chance to send.
    const [audienceLoading, setAudienceLoading] = useState(false);
    const [showBlocked, setShowBlocked] = useState(false);
    // Bumped after an audience save so eligibility is re-fetched in place.
    const [audienceRefreshKey, setAudienceRefreshKey] = useState(0);

    useEffect(() => {
        if (!open || !leadId || !wpAccountId || !practiceId || !canViewAudience) {
            setAudienceMap({});
            setAudienceChecked({});
            setAudienceLoading(false);
            return undefined;
        }
        let cancelled = false;
        setAudienceLoading(true);
        callApi(
            `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${wpAccountId}/whatsapp-message-templates/?for_lead=${leadId}&limit=500`
        )
            .then((res) => {
                if (cancelled) return;
                const rows = res?.status === 200 ? (res.data?.results || []) : [];
                const next = {};
                const checked = {};
                rows.forEach((row) => {
                    // A null verdict means the backend did not evaluate this row
                    // (feature off, or the lead did not resolve) — deliberately
                    // NOT recorded as checked.
                    if (row?.is_eligible_for_lead === true || row?.is_eligible_for_lead === false) {
                        checked[row.id] = true;
                    }
                    if (row?.is_eligible_for_lead === false) {
                        next[row.id] = row.audience_blocked_reason?.message
                            || 'This template cannot be used for this Lead';
                    }
                });
                setAudienceMap(next);
                setAudienceChecked(checked);
            })
            .catch(() => {
                // Leave `checked` empty so restricted templates fall through to
                // the unverified branch and stay un-sendable.
                if (!cancelled) { setAudienceMap({}); setAudienceChecked({}); }
            })
            .finally(() => { if (!cancelled) setAudienceLoading(false); });
        return () => { cancelled = true; };
    }, [open, leadId, wpAccountId, practiceId, canViewAudience, audienceRefreshKey]);

    useEffect(() => { if (!open) setShowBlocked(false); }, [open]);

    /**
     * Why this template cannot go to the current lead — '' when it can.
     *
     * Fails CLOSED: a template carrying a restrictive rule that the backend did
     * NOT return a verdict for is treated as un-sendable. Templates with no
     * rule can never be blocked, so they are never caught by this and the
     * blast radius stays at exactly the rows in question.
     */
    const audienceBlockFor = useCallback((template) => {
        if (!canViewAudience || !leadId || template?.id == null) return '';
        if (audienceMap[template.id]) return audienceMap[template.id];
        if (audienceChecked[template.id]) return '';
        const summary = audienceSummary(template);
        if (!summary.restricted) return '';
        return audienceLoading
            ? 'Checking whether this template can be used for this Lead…'
            : 'Cannot confirm this template is allowed for this Lead';
    }, [canViewAudience, leadId, audienceMap, audienceChecked, audienceLoading]);

    const isBlockedForLead = useCallback(
        (template) => Boolean(audienceBlockFor(template)),
        [audienceBlockFor],
    );

    const refreshSelectorTemplates = useCallback(async (targetTemplateName = '') => {
        if (!wpAccountId || !practiceId) return null;
        dispatch(getSearchWpTemplateList({ wpAccountId }));
        const nextTemplates = await dispatch(refreshWhatsappTemplatesForBusiness({
            practiceId,
            businessId: wpAccountId,
        }));
        if (targetTemplateName && Array.isArray(nextTemplates)) {
            return nextTemplates.find((template) => (
                template.name === targetTemplateName && isNormalWhatsAppTemplate(template)
            )) || null;
        }
        return null;
    }, [wpAccountId, practiceId]);

    // Declared AFTER refreshSelectorTemplates on purpose: it names that
    // callback in its dependency array, and a const is in the temporal dead
    // zone until its own declaration runs.
    const handleUpdateAudience = useCallback(async (payload, id) => {
        if (!wpAccountId || !practiceId || !id) return false;
        const res = await callApi(
            `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${wpAccountId}/whatsapp-message-templates/${id}/`,
            'PATCH',
            payload,
        );
        if (res?.status !== 200) {
            dispatch(updateError({
                message: res?.data?.detail || 'Could not update audience',
                success: false,
                status: res?.status,
            }));
            return false;
        }
        // AWAIT the refresh, and refresh the source the selector actually
        // renders from. Dispatching getSearchWpTemplateList alone updated a
        // different slice, so the row kept its old audience until a full page
        // reload — which is what made this feel broken.
        await refreshSelectorTemplates();
        // Re-run eligibility: the rule that just changed may have made this
        // template newly (in)eligible for the patient in the open conversation.
        setAudienceRefreshKey((k) => k + 1);
        dispatch(updateError({ message: 'Audience updated', success: true, timeout: 3000 }));
        return true;
    }, [wpAccountId, practiceId, refreshSelectorTemplates]);

    // Ensure whatsapp account data is loaded (needed for create/edit API calls)
    useEffect(() => {
        if (open && !wpAccountId && practiceId) {
            dispatch(getWhatsappAccount(practiceId));
        }
    }, [open, wpAccountId, practiceId]);

    // Refresh template list when the selector opens
    useEffect(() => {
        if (open && wpAccountId) {
            refreshSelectorTemplates();
        }
    }, [open, wpAccountId, refreshSelectorTemplates]);

    useEffect(() => {
        if (open && !prevOpenRef.current) {
            setSearchText('');
            setSelectedCategory(null);
            // Seed from initialStatus prop so callers can open pre-filtered
            setSelectedStatus(initialStatus || 'ALL');
            setMobileShowList(isMobile);
            setTemplateSubmitFeedback(null);
            setPendingTemplateName('');
            if (normalTemplates.length > 0 && !hasExternalSelection) {
                setInternalSelectedTemplate(normalTemplates[0]);
            }
        }
        prevOpenRef.current = open;
    }, [open, normalTemplates, hasExternalSelection, initialStatus, isMobile]);

    // A refreshed list can remove or reclassify the current internal choice.
    // Move to the first remaining normal template so preview/edit/send never
    // retain an invisible Personal Reply or deleted template.
    useEffect(() => {
        if (hasExternalSelection) return;
        setInternalSelectedTemplate((current) => {
            const refreshed = findAvailableTemplate(current);
            return refreshed || normalTemplates[0] || null;
        });
    }, [hasExternalSelection, normalTemplates, findAvailableTemplate]);

    useEffect(() => () => {
        if (favouriteFeedbackTimerRef.current) clearTimeout(favouriteFeedbackTimerRef.current);
    }, []);

    const usageTypes = useMemo(() => {
        // Fixed allowlist — hides system/internal types (OTHER, AUTOMATION,
        // NOTIFICATION). No "All" chip: null selectedCategory = show all.
        // Each allowed type surfaces only when at least one template uses it
        // (no empty chips). FAVOURITES always shown.
        const CATEGORY_ALLOWLIST = ['FOLLOW_UP', 'FORMS'];
        const presentTypes = CATEGORY_ALLOWLIST.filter(t =>
            normalTemplates.some(tmpl => tmpl.usage_type === t)
        );
        return ['FAVOURITES', ...presentTypes];
    }, [normalTemplates]);

    const usageTypeCounts = useMemo(() => {
        const counts = {
            FAVOURITES: normalTemplates.filter(t => t.is_favourite).length,
        };
        normalTemplates.forEach(t => {
            const type = t.usage_type || 'GENERAL';
            counts[type] = (counts[type] || 0) + 1;
        });
        return counts;
    }, [normalTemplates]);

    // Status-bucket counts (for tab badges)
    const statusCounts = useMemo(() => ({
        ALL: normalTemplates.length,
        APPROVED: normalTemplates.filter(t => normTemplateStatus(t) === 'APPROVED').length,
        PENDING: normalTemplates.filter(t => normTemplateStatus(t) === 'PENDING').length,
        REJECTED: normalTemplates.filter(t => normTemplateStatus(t) === 'REJECTED').length,
    }), [normalTemplates]);

    // Blocked templates are hidden by default and revealed on request, so an
    // operator who knows a template exists is told WHY it is unavailable rather
    // than left hunting for it.
    const blockedForLead = useMemo(
        () => normalTemplates.filter(isBlockedForLead),
        [normalTemplates, isBlockedForLead],
    );

    const filteredTemplates = useMemo(() => {
        let filtered = normalTemplates;

        if (!showBlocked) {
            filtered = filtered.filter((t) => !isBlockedForLead(t));
        }

        // Search filter
        if (searchText) {
            filtered = filtered.filter(t =>
                matchesSearch(t.name, searchText) ||
                getTemplateComponents(t).some(c => String(c?.text || '').toLowerCase().includes(searchText.toLowerCase().trim()))
            );
        }

        // Usage-type / favourites filter (null = no filter = show all)
        if (selectedCategory === 'FAVOURITES') {
            filtered = filtered.filter(t => t.is_favourite);
        } else if (selectedCategory) {
            filtered = filtered.filter(t => t.usage_type === selectedCategory);
        }

        // Status filter
        if (selectedStatus !== 'ALL') {
            filtered = filtered.filter(t => normTemplateStatus(t) === selectedStatus);
        }

        // When no category/status filter is active, sort favourites to the top
        if (!selectedCategory && selectedStatus === 'ALL' && !searchText) {
            filtered = [...filtered].sort((a, b) => (b.is_favourite ? 1 : 0) - (a.is_favourite ? 1 : 0));
        }

        return filtered;
    }, [normalTemplates, searchText, selectedCategory, selectedStatus, showBlocked, isBlockedForLead]);

    const handleTemplateSelect = useCallback((template) => {
        if (!isNormalWhatsAppTemplate(template)) return;
        setInternalSelectedTemplate(template);
        if (isMobile) setMobileShowList(false);
        onSelect?.(template);
    }, [isMobile, onSelect]);

    useEffect(() => {
        if (!pendingTemplateName || !Array.isArray(templates)) return;
        const createdTemplate = templates.find((template) => template.name === pendingTemplateName);
        if (!createdTemplate) return;
        setPendingTemplateName('');
        if (isNormalWhatsAppTemplate(createdTemplate)) {
            handleTemplateSelect(createdTemplate);
        }
    }, [pendingTemplateName, templates, handleTemplateSelect]);

    const handleSend = useCallback(() => {
        // Blocked templates are only reachable via the "Show" reveal, and the
        // backend refuses them anyway — but keeping the guard here means the
        // keyboard shortcut path cannot slip past the UI rule either.
        if (isBlockedForLead(selectedTemplate)) return;
        if (canWriteProvider && isNormalWhatsAppTemplate(selectedTemplate)) {
            onSend?.(selectedTemplate);
        }
    }, [canWriteProvider, selectedTemplate, onSend, isBlockedForLead]);

    // Favourite toggle — optimistic via Redux thunk
    const handleToggleFavourite = useCallback((template) => {
        if (!canWriteProvider || !wpAccountId || !practiceId) return;
        const nextFavourite = !template.is_favourite;
        setFavouriteFeedback(nextFavourite ? 'Added to favourites' : 'Removed from favourites');
        if (favouriteFeedbackTimerRef.current) clearTimeout(favouriteFeedbackTimerRef.current);
        favouriteFeedbackTimerRef.current = setTimeout(() => setFavouriteFeedback(''), 1600);
        dispatch(toggleTemplateFavourite({
            practiceId,
            wpAccountId,
            templateId: template.id,
            newFavourite: nextFavourite,
        }));
    }, [canWriteProvider, wpAccountId, practiceId]);

    // Sync — triggers Meta's sync_templates, then refreshes wpTemplateListGlobal.
    // syncTemplates() updates whatsappV2 slice; we follow it with
    // getSearchWpTemplateList to also update the slice this selector reads.
    const handleSync = useCallback(async () => {
        if (!canWriteProvider || syncing) return;
        setSyncing(true);
        try {
            await dispatch(syncTemplates());
            if (wpAccountId) dispatch(getSearchWpTemplateList({ wpAccountId }));
        } finally {
            setSyncing(false);
        }
    }, [canWriteProvider, syncing, wpAccountId]);

    // ── Built-in create handler ──
    const handleCreateTemplate = useCallback(async (payload) => {
        if (!canWriteProvider || !wpAccountId) return;
        setSubmitting(true);
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${wpAccountId}/whatsapp-message-templates/create_from_meta/`,
                'POST',
                payload
            );
            if (res.ok) {
                const tplName = res.data?.template_name || '';
                const displayName = tplName ? formatTemplateName(tplName) : 'Template';
                dispatch(updateError({
                    message: `"${displayName}" submitted to Meta — awaiting approval. It will appear under Pending until Meta approves it.`,
                    success: true,
                }));
                setCreateDialogOpen(false);
                // Route to Pending (no category filter) so the new template is visible.
                setSelectedCategory(null);
                setSelectedStatus('PENDING');
                setSearchText('');
                setPendingTemplateName(tplName);
                setTemplateSubmitFeedback({
                    tone: 'success',
                    icon: 'mdi:clock-check-outline',
                    title: `${displayName} submitted`,
                    body: 'It is now under Pending while Meta reviews it. Once approved, it can be sent from this picker.',
                });
                const createdTemplate = await refreshSelectorTemplates(tplName);
                if (createdTemplate) {
                    handleTemplateSelect(createdTemplate);
                }
            } else {
                const errMsg = res.data?.detail || res.data?.error || 'Failed to create template';
                setTemplateSubmitFeedback({
                    tone: 'error',
                    icon: 'mdi:alert-circle-outline',
                    title: 'Template was not submitted',
                    body: typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg),
                });
                dispatch(updateError({ message: typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg), success: false }));
            }
        } catch {
            setTemplateSubmitFeedback({
                tone: 'error',
                icon: 'mdi:alert-circle-outline',
                title: 'Template was not submitted',
                body: 'There was a problem submitting the template. Please try again.',
            });
            dispatch(updateError({ message: 'Failed to create template', success: false }));
        } finally {
            setSubmitting(false);
        }
    }, [canWriteProvider, wpAccountId, practiceId, refreshSelectorTemplates, handleTemplateSelect]);

    // ── Built-in edit handler ──
    const handleEditTemplate = useCallback(async (payload) => {
        if (!canWriteProvider || !wpAccountId || !payload.templateId) return;
        setSubmitting(true);
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${wpAccountId}/whatsapp-message-templates/${payload.templateId}/update_from_meta/`,
                'POST',
                { template: payload.template, components: payload.components }
            );
            if (res.ok) {
                const templateName = payload.template?.name ? formatTemplateName(payload.template.name) : 'Template';
                dispatch(updateError({
                    message: `${templateName} updated and submitted to Meta — awaiting approval.`,
                    success: true,
                }));
                setCreateDialogOpen(false);
                setEditingTemplate(null);
                // Route to Pending so the user can see the re-submitted template.
                setSelectedCategory(null);
                setSelectedStatus('PENDING');
                setSearchText('');
                setTemplateSubmitFeedback({
                    tone: 'success',
                    icon: 'mdi:clock-check-outline',
                    title: `${templateName} resubmitted`,
                    body: 'It is now under Pending while Meta reviews the latest changes.',
                });
                await refreshSelectorTemplates(payload.template?.name || '');
            } else {
                const errMsg = res.data?.detail || res.data?.error || 'Failed to update template';
                setTemplateSubmitFeedback({
                    tone: 'error',
                    icon: 'mdi:alert-circle-outline',
                    title: 'Template was not updated',
                    body: typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg),
                });
                dispatch(updateError({ message: typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg), success: false }));
            }
        } catch {
            setTemplateSubmitFeedback({
                tone: 'error',
                icon: 'mdi:alert-circle-outline',
                title: 'Template was not updated',
                body: 'There was a problem submitting the update. Please try again.',
            });
            dispatch(updateError({ message: 'Failed to update template', success: false }));
        } finally {
            setSubmitting(false);
        }
    }, [canWriteProvider, wpAccountId, practiceId, refreshSelectorTemplates]);

    // ── Combined submit router ──
    const handleDialogSubmit = useCallback(async (payload) => {
        if (payload.isEdit) {
            await handleEditTemplate(payload);
        } else {
            await handleCreateTemplate(payload);
        }
    }, [handleCreateTemplate, handleEditTemplate]);

    // ── Open create dialog — prefer external handler if provided ──
    const handleOpenCreate = useCallback(() => {
        if (!canWriteProvider) return;
        if (onRequestTemplate) {
            onRequestTemplate();
        } else {
            setEditingTemplate(null);
            setCreateDialogOpen(true);
        }
    }, [canWriteProvider, onRequestTemplate]);

    // ── Open edit dialog — prefer external handler if provided ──
    const handleOpenEdit = useCallback(() => {
        if (!canWriteProvider) return;
        if (onEditTemplate) {
            onEditTemplate(selectedTemplate);
        } else if (selectedTemplate) {
            setEditingTemplate(selectedTemplate);
            setCreateDialogOpen(true);
        }
    }, [canWriteProvider, onEditTemplate, selectedTemplate]);

    const emptyState = useMemo(() => {
        const trimmedSearch = searchText.trim();
        if (selectedCategory === 'FAVOURITES') {
            return {
                icon: 'mdi:star-outline',
                title: 'No favourite templates yet',
                body: 'Star templates you use often and they will appear here.',
                action: 'Show all templates',
                onAction: () => setSelectedCategory(null),
            };
        }
        if (trimmedSearch) {
            return {
                icon: 'mdi:file-search-outline',
                title: `No results for "${trimmedSearch}"`,
                body: 'Try a different search or clear the current filters.',
                action: 'Clear search',
                onAction: () => {
                    setSearchText('');
                    setSelectedCategory(null);
                    setSelectedStatus('ALL');
                },
            };
        }
        if (selectedStatus === 'APPROVED') {
            return {
                icon: 'mdi:check-decagram-outline',
                title: 'No approved templates',
                body: 'Approved templates can be sent to patients. Check pending templates or create a new one.',
                action: 'Create template',
                onAction: handleOpenCreate,
            };
        }
        if (selectedStatus === 'PENDING') {
            return {
                icon: 'mdi:clock-outline',
                title: 'No pending templates',
                body: 'Templates awaiting Meta approval will appear here.',
                action: 'Show all templates',
                onAction: () => setSelectedStatus('ALL'),
            };
        }
        if (selectedStatus === 'REJECTED') {
            return {
                icon: 'mdi:alert-circle-outline',
                title: 'No rejected templates',
                body: 'Rejected or disabled templates will appear here if Meta blocks them.',
                action: 'Show all templates',
                onAction: () => setSelectedStatus('ALL'),
            };
        }
        return {
            icon: 'mdi:file-document-plus-outline',
            title: 'No templates found',
            body: 'Create a WhatsApp template to start conversations outside the 24-hour window.',
            action: 'Create template',
            onAction: handleOpenCreate,
        };
    }, [searchText, selectedCategory, selectedStatus, handleOpenCreate]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!open || createDialogOpen) return;
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                const currentIndex = filteredTemplates.findIndex(t => t.id === selectedTemplate?.id);
                let newIndex;
                if (e.key === 'ArrowDown') {
                    newIndex = currentIndex < filteredTemplates.length - 1 ? currentIndex + 1 : 0;
                } else {
                    newIndex = currentIndex > 0 ? currentIndex - 1 : filteredTemplates.length - 1;
                }
                handleTemplateSelect(filteredTemplates[newIndex]);
            }
            if (e.key === 'Enter' && selectedTemplate) {
                handleSend();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open, createDialogOpen, filteredTemplates, selectedTemplate, handleTemplateSelect, handleSend]);

    return (
        <>
            <Modal open={open} onClose={onClose}>
                <m.div
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.25, ease: 'easeOut' }}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%',
                    }}
                >
                <Box
                    sx={{
                        width: isSmall ? '95vw' : '90vw',
                        maxWidth: 960,
                        maxHeight: isMobile ? '100dvh' : '80vh',
                        height: isMobile ? '100dvh' : (isSmall ? '95vh' : '80vh'),
                        bgcolor: '#fff',
                        borderRadius: '16px',
                        border: '0.5px solid rgba(0,0,0,0.06)',
                        boxShadow: '0 24px 80px rgba(0,0,0,0.12), 0 8px 24px rgba(0,0,0,0.08), 0 0 0 0.5px rgba(0,0,0,0.04)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'auto',
                    }}
                >
                    {/* ── Header ── */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 2.5,
                            py: 1.75,
                            borderBottom: '0.5px solid rgba(0,0,0,0.08)',
                            bgcolor: '#fff',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '50%',
                                    bgcolor: WA_LIGHT_GREEN,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#fff',
                                    flexShrink: 0,
                                }}
                            >
                                <Icon icon="mdi:whatsapp" width={20} />
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: 650, fontSize: '0.92rem', lineHeight: 1.3, color: 'text.primary', letterSpacing: '-0.01em' }}>
                                    {title}
                                </Typography>
                                <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', lineHeight: 1.3 }}>
                                    {normalTemplates.length} template{normalTemplates.length !== 1 ? 's' : ''} available
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Button
                                size="small"
                                onClick={handleOpenCreate}
                                disabled={!canWriteProvider}
                                startIcon={<Icon icon="mdi:plus" width={16} />}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '0.76rem',
                                    borderRadius: APPLE.segmentedRadius,
                                    px: 1.5,
                                    py: 0.5,
                                    bgcolor: 'rgba(37,211,102,0.08)',
                                    color: WA_GREEN,
                                    border: '0.5px solid rgba(37,211,102,0.2)',
                                    '&:hover': {
                                        bgcolor: 'rgba(37,211,102,0.14)',
                                        border: '0.5px solid rgba(37,211,102,0.3)',
                                    },
                                }}
                            >
                                Create Template
                            </Button>
                            <IconButton
                                onClick={onClose}
                                size="small"
                                sx={{
                                    width: 30,
                                    height: 30,
                                    color: 'text.secondary',
                                    bgcolor: 'rgba(0,0,0,0.04)',
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
                                }}
                            >
                                <Icon icon="mdi:close" width={18} />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* ── Content ── */}
                    <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
                        {/* Left Panel: Search + Filters + List */}
                        <Box
                            sx={{
                                width: isMobile ? '100%' : 320,
                                flexShrink: 0,
                                borderRight: isMobile ? 'none' : '0.5px solid rgba(0,0,0,0.08)',
                                display: isMobile && selectedTemplate && !mobileShowList ? 'none' : 'flex',
                                flexDirection: 'column',
                                bgcolor: '#fff',
                            }}
                        >
                            {/* Search */}
                            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Search templates..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Icon icon="mdi:magnify" width={18} color="rgba(0,0,0,0.3)" />
                                            </InputAdornment>
                                        ),
                                        sx: { fontSize: '0.84rem', height: 38 },
                                    }}
                                    sx={appleInputSx}
                                />
                            </Box>

                            {/* Category chips + conditional status pills on the same row.
                                Pills only render when their count > 0 — zero status clutter
                                for practices with only approved templates. Clicking a pill
                                sets selectedStatus; clicking the active pill resets to ALL. */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    px: 2,
                                    pt: 1,
                                    pb: 1.5,
                                    overflowX: 'auto',
                                    '&::-webkit-scrollbar': { height: 0 },
                                }}
                            >
                                {usageTypes.map((type) => {
                                    const isActive = selectedCategory === type;
                                    const isFavouritesTab = type === 'FAVOURITES';
                                    // FAVOURITES uses amber; other types use their category colour
                                    const chipColor = isFavouritesTab
                                        ? { color: '#f59e0b' }
                                        : getUsageTypeColor(type, theme);
                                    const count = usageTypeCounts[type] || 0;
                                    const label = isFavouritesTab
                                        ? `Favourites${count > 0 ? ` (${count})` : ''}`
                                        : `${formatUsageType(type)} (${count})`;
                                    return (
                                        <Chip
                                            key={type}
                                            label={label}
                                            // Toggle: tap active chip to deselect (show all)
                                            onClick={() => setSelectedCategory(prev => prev === type ? null : type)}
                                            size="small"
                                            sx={{
                                                height: 22,
                                                fontSize: '0.64rem',
                                                fontWeight: 600,
                                                borderRadius: '6px',
                                                flexShrink: 0,
                                                '& .MuiChip-label': { px: 0.75 },
                                                ...(isActive ? {
                                                    bgcolor: chipColor.color,
                                                    color: '#fff',
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                                                    '&:hover': { bgcolor: chipColor.color },
                                                } : {
                                                    bgcolor: 'rgba(0,0,0,0.04)',
                                                    color: 'text.secondary',
                                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
                                                }),
                                            }}
                                        />
                                    );
                                })}

                                {/* Status count pills — rendered only when count > 0.
                                    Clicking toggles selectedStatus; clicking active pill resets to ALL.
                                    ml:'auto' on the first pill pushes both pills to the right end. */}
                                {statusCounts.PENDING > 0 && (
                                    <Chip
                                        label={`⏳ ${statusCounts.PENDING} Pending`}
                                        size="small"
                                        onClick={() => setSelectedStatus(v => v === 'PENDING' ? 'ALL' : 'PENDING')}
                                        sx={{
                                            ml: 'auto',
                                            flexShrink: 0,
                                            height: 22,
                                            fontSize: '0.64rem',
                                            fontWeight: 600,
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            '& .MuiChip-label': { px: 0.75 },
                                            ...(selectedStatus === 'PENDING' ? {
                                                bgcolor: '#b45309',
                                                color: '#fff',
                                            } : {
                                                bgcolor: 'rgba(245,158,11,0.1)',
                                                color: '#b45309',
                                            }),
                                        }}
                                    />
                                )}

                                {/* Sync button — inline next to Pending pill when active */}
                                {selectedStatus === 'PENDING' && (
                                    <Tooltip
                                        title={syncing ? 'Checking approval status…' : 'Check approval status with Meta'}
                                        placement="left"
                                        arrow
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <IconButton
                                                size="small"
                                                onClick={handleSync}
                                                disabled={!canWriteProvider || syncing}
                                                sx={{
                                                    width: 26,
                                                    height: 26,
                                                    borderRadius: '6px',
                                                    color: syncing ? '#b45309' : 'text.disabled',
                                                    '&:hover': { bgcolor: 'rgba(245,158,11,0.08)', color: '#b45309' },
                                                    '&.Mui-disabled': { color: '#b45309' },
                                                }}
                                            >
                                                {syncing
                                                    ? <CircularProgress size={13} thickness={5} sx={{ color: '#b45309' }} />
                                                    : <Icon icon="mdi:refresh" width={14} />}
                                            </IconButton>
                                        </Box>
                                    </Tooltip>
                                )}

                                {statusCounts.REJECTED > 0 && (
                                    <Chip
                                        label={`⚠ ${statusCounts.REJECTED} Rejected`}
                                        size="small"
                                        onClick={() => setSelectedStatus(v => v === 'REJECTED' ? 'ALL' : 'REJECTED')}
                                        sx={{
                                            // Only push right when there's no Pending pill to do it
                                            ml: statusCounts.PENDING === 0 ? 'auto' : undefined,
                                            flexShrink: 0,
                                            height: 22,
                                            fontSize: '0.64rem',
                                            fontWeight: 600,
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            '& .MuiChip-label': { px: 0.75 },
                                            ...(selectedStatus === 'REJECTED' ? {
                                                bgcolor: '#dc2626',
                                                color: '#fff',
                                            } : {
                                                bgcolor: 'rgba(239,68,68,0.1)',
                                                color: '#dc2626',
                                            }),
                                        }}
                                    />
                                )}
                            </Box>

                            {/* Audience-hidden count. One quiet line rather than a
                                banner — most sends never see it, and when it does
                                appear the operator needs the reason, not alarm. */}
                            {blockedForLead.length > 0 && (
                                <Box sx={{ px: 2, pb: 1 }}>
                                    <Box
                                        onClick={() => setShowBlocked((v) => !v)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                            px: 1.25,
                                            py: 0.75,
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            bgcolor: 'rgba(145,158,171,0.08)',
                                            '&:hover': { bgcolor: 'rgba(145,158,171,0.14)' },
                                        }}
                                    >
                                        <Icon icon="mdi:account-cancel-outline" width={14} color="#919EAB" />
                                        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', flex: 1 }}>
                                            {blockedForLead.length} template{blockedForLead.length === 1 ? '' : 's'} not
                                            allowed for this patient
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: WA_GREEN }}>
                                            {showBlocked ? 'Hide' : 'Show'}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {templateSubmitFeedback && (
                                <Box sx={{ px: 2, pb: 1 }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 1,
                                            p: 1.25,
                                            borderRadius: '10px',
                                            bgcolor: templateSubmitFeedback.tone === 'error'
                                                ? 'rgba(239,68,68,0.08)'
                                                : 'rgba(37,211,102,0.08)',
                                            border: templateSubmitFeedback.tone === 'error'
                                                ? '0.5px solid rgba(239,68,68,0.18)'
                                                : '0.5px solid rgba(37,211,102,0.18)',
                                        }}
                                    >
                                        <Icon
                                            icon={templateSubmitFeedback.icon}
                                            width={18}
                                            style={{
                                                color: templateSubmitFeedback.tone === 'error' ? '#dc2626' : WA_GREEN,
                                                flexShrink: 0,
                                                marginTop: 1,
                                            }}
                                        />
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography
                                                sx={{
                                                    fontSize: '0.76rem',
                                                    fontWeight: 700,
                                                    color: templateSubmitFeedback.tone === 'error' ? '#991b1b' : WA_GREEN,
                                                    lineHeight: 1.25,
                                                }}
                                            >
                                                {templateSubmitFeedback.title}
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.35, lineHeight: 1.35 }}>
                                                {templateSubmitFeedback.body}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                            {favouriteFeedback && (
                                <Box sx={{ px: 2, pb: 1 }}>
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: '8px',
                                            bgcolor: 'rgba(245,158,11,0.1)',
                                            color: '#b45309',
                                        }}
                                    >
                                        <Icon icon="mdi:star" width={13} />
                                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                                            {favouriteFeedback}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {/* Template List */}
                            <Box
                                sx={{
                                    flex: 1,
                                    overflowY: 'auto',
                                    borderTop: '0.5px solid rgba(0,0,0,0.06)',
                                    '&::-webkit-scrollbar': { width: 4 },
                                    '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.1)', borderRadius: 4 },
                                }}
                            >
                                {loading ? (
                                    <Box sx={{ p: 3, textAlign: 'center' }}>
                                        <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>Loading templates...</Typography>
                                    </Box>
                                ) : filteredTemplates.length === 0 ? (
                                    <Box sx={{ p: 4, textAlign: 'center' }}>
                                        <Icon icon={emptyState.icon} width={40} color="rgba(0,0,0,0.18)" />
                                        <Typography sx={{ fontSize: '0.86rem', color: 'text.primary', mt: 1, fontWeight: 650 }}>
                                            {emptyState.title}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary', mt: 0.5, lineHeight: 1.4 }}>
                                            {emptyState.body}
                                        </Typography>
                                        <Button
                                            size="small"
                                            onClick={emptyState.onAction}
                                            startIcon={<Icon icon={emptyState.action === 'Create template' ? 'mdi:plus' : 'mdi:filter-remove-outline'} width={14} />}
                                            sx={{
                                                mt: 1.5,
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                fontSize: '0.76rem',
                                                borderRadius: APPLE.segmentedRadius,
                                                color: WA_GREEN,
                                            }}
                                        >
                                            {emptyState.action}
                                        </Button>
                                    </Box>
                                ) : (
                                    <List disablePadding>
                                        {filteredTemplates.map((template) => {
                                            const blockedReason = audienceBlockFor(template);
                                            // Restricted-but-allowed rows get a quiet summary;
                                            // blocked rows get the red chip. Both carry the full
                                            // sentence on the row's info icon.
                                            const summary = canViewAudience
                                                ? audienceSummary(template)
                                                : { restricted: false, label: '' };
                                            const item = (
                                                <WhatsAppTemplateListItem
                                                    key={template.id}
                                                    template={template}
                                                    isSelected={selectedTemplate?.id === template.id}
                                                    onClick={() => { if (!blockedReason) handleTemplateSelect(template); }}
                                                    onToggleFavourite={canWriteProvider ? handleToggleFavourite : undefined}
                                                    audienceBlockedReason={blockedReason || ''}
                                                    audienceSummary={summary.restricted ? summary.label : ''}
                                                />
                                            );
                                            if (!blockedReason) return item;
                                            return (
                                                <Box key={template.id} sx={{ opacity: 0.55, cursor: 'not-allowed' }}>
                                                    {item}
                                                </Box>
                                            );
                                        })}
                                    </List>
                                )}
                            </Box>
                        </Box>

                        {/* Right Panel: Preview */}
                        <Box
                            sx={{
                                flex: 1,
                                display: isMobile && mobileShowList ? 'none' : 'flex',
                                flexDirection: 'column',
                                bgcolor: '#fafafa',
                                overflow: 'hidden',
                            }}
                        >
                            {/* Mobile back button */}
                            {isMobile && selectedTemplate && !mobileShowList && (
                                <Box sx={{ p: 1, borderBottom: '0.5px solid rgba(0,0,0,0.06)', bgcolor: '#fff' }}>
                                    <Button
                                        startIcon={<Icon icon="mdi:arrow-left" />}
                                        onClick={() => setMobileShowList(true)}
                                        size="small"
                                        sx={{ fontSize: '0.75rem', textTransform: 'none', color: APPLE.blue, fontWeight: 500 }}
                                    >
                                        Back to templates
                                    </Button>
                                </Box>
                            )}

                            {/* Phone mockup preview — scrollable */}
                            <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2.5, px: 2 }}>
                                <WhatsAppTemplatePreviewPanel
                                    template={selectedTemplate}
                                    contextMapping={contextMapping}
                                    showVariables
                                />
                            </Box>

                            {/* Footer: Edit + Cancel + Send */}
                            {selectedTemplate && (
                                <Box
                                    sx={{
                                        px: 2.5,
                                        py: 1.5,
                                        borderTop: '0.5px solid rgba(0,0,0,0.08)',
                                        bgcolor: '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexWrap: isSmall ? 'wrap' : 'nowrap',
                                        gap: 1.5,
                                    }}
                                >
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography
                                            sx={{
                                                fontSize: '0.72rem',
                                                fontWeight: 700,
                                                color: 'text.primary',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {selectedTemplate?.name ? formatTemplateName(selectedTemplate.name) : 'Selected template'}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', mt: 0.2 }}>
                                            {(() => {
                                                const statusBucket = normTemplateStatus(selectedTemplate);
                                                if (statusBucket === 'PENDING') return 'Awaiting Meta approval before it can be sent';
                                                if (statusBucket === 'REJECTED') return selectedTemplate.status_reason || 'Rejected by Meta and cannot be sent';
                                                // Audience is the other reason a template cannot go
                                                // out. Saying so HERE — next to the Send button —
                                                // beats saying it in a toast after the click.
                                                const blocked = audienceBlockFor(selectedTemplate);
                                                if (blocked) return blocked;
                                                const summary = canViewAudience
                                                    ? audienceSummary(selectedTemplate)
                                                    : { restricted: false, label: '' };
                                                // The rule, phrased as a rule. "Ready to send ·
                                                // Excludes 1 treatment" says this lead qualifies
                                                // AND the template has limits — where the old
                                                // "1 treatment blocked" read as a contradiction
                                                // next to an enabled Send button.
                                                if (summary.restricted) return `Ready to send · ${summary.label}`;
                                                return 'Ready to send';
                                            })()}
                                        </Typography>
                                    </Box>

                                    <Tooltip
                                        title="Edit this template. Once edited, it will be sent for Meta approval. Templates can only be edited once every 24 hours."
                                        arrow
                                        placement="top"
                                    >
                                        <span>
                                            <Button
                                                size="small"
                                                onClick={handleOpenEdit}
                                                disabled={!canWriteProvider}
                                                startIcon={<Icon icon="mdi:pencil-outline" width={15} />}
                                                sx={{
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    fontSize: '0.78rem',
                                                    borderRadius: APPLE.inputRadius,
                                                    px: 2,
                                                    py: 0.65,
                                                    color: APPLE.blue,
                                                    bgcolor: 'transparent',
                                                    border: '0.5px solid rgba(0,122,255,0.16)',
                                                    '&:hover': {
                                                        bgcolor: 'rgba(0,122,255,0.1)',
                                                        border: '0.5px solid rgba(0,122,255,0.25)',
                                                    },
                                                }}
                                            >
                                                Edit
                                            </Button>
                                        </span>
                                    </Tooltip>

                                    <Button
                                        onClick={onClose}
                                        size="small"
                                        sx={{
                                            px: 2.5,
                                            py: 0.65,
                                            fontWeight: 600,
                                            fontSize: '0.8rem',
                                            borderRadius: APPLE.inputRadius,
                                            textTransform: 'none',
                                            bgcolor: 'rgba(0,0,0,0.04)',
                                            color: 'text.secondary',
                                            '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    {(() => {
                                        const statusBucket = normTemplateStatus(selectedTemplate);
                                        const audienceReason = audienceBlockFor(selectedTemplate);
                                        const isSendable = canWriteProvider
                                            && !audienceReason
                                            && !audienceLoading
                                            && (!statusBucket || statusBucket === 'APPROVED');
                                        const sendTooltip = audienceReason
                                            || (audienceLoading ? 'Checking who this template can reach…' : '')
                                            || (statusBucket === 'PENDING'
                                            ? 'Awaiting Meta approval — cannot be sent yet'
                                            : statusBucket === 'REJECTED'
                                            ? 'This template was rejected by Meta and cannot be sent'
                                            : '');
                                        return (
                                            <Tooltip title={sendTooltip} arrow placement="top" disableHoverListener={isSendable}>
                                                <span>
                                                    <Button
                                                        variant="contained"
                                                        onClick={handleSend}
                                                        disabled={!isSendable}
                                                        startIcon={<Icon icon={isSendable ? 'mdi:send' : 'mdi:clock-outline'} width={15} />}
                                                        size="small"
                                                        sx={{
                                                            px: 3,
                                                            py: 0.8,
                                                            minWidth: 138,
                                                            fontWeight: 700,
                                                            fontSize: '0.8rem',
                                                            borderRadius: APPLE.inputRadius,
                                                            textTransform: 'none',
                                                            ...(isSendable ? {
                                                                bgcolor: WA_LIGHT_GREEN,
                                                                color: '#fff',
                                                                boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 0 16px rgba(37,211,102,0.2)',
                                                                '&:hover': { bgcolor: WA_GREEN, boxShadow: '0 2px 8px rgba(0,0,0,0.16), 0 0 20px rgba(37,211,102,0.25)' },
                                                            } : {}),
                                                        }}
                                                    >
                                                        {sendButtonLabel({ audienceReason, audienceLoading, statusBucket })}
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        );
                                    })()}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
                </m.div>
            </Modal>

            {/* ── Built-in Create / Edit Dialog ── */}
            <RequestTemplateDialog
                open={createDialogOpen}
                onClose={() => {
                    setCreateDialogOpen(false);
                    setEditingTemplate(null);
                }}
                onSubmit={handleDialogSubmit}
                submitting={submitting}
                editTemplate={editingTemplate}
                existingTemplateNames={existingNames}
                onUpdateAudience={canManageAudience && canWriteProvider ? handleUpdateAudience : undefined}
                practiceTreatments={practiceTreatments}
            />
        </>
    );
};

WhatsAppTemplateSelector.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    templates: PropTypes.array,
    onSend: PropTypes.func,
    onSelect: PropTypes.func,
    selectedTemplate: PropTypes.object,
    loading: PropTypes.bool,
    contextMapping: PropTypes.object,
    title: PropTypes.string,
    onRequestTemplate: PropTypes.func,
    onEditTemplate: PropTypes.func,
    providerAccount: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        is_connected: PropTypes.bool,
    }),
    initialStatus: PropTypes.oneOf(['ALL', 'APPROVED', 'PENDING', 'REJECTED']),
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

WhatsAppTemplateSelector.defaultProps = {
    templates: [],
    onSend: undefined,
    onSelect: undefined,
    selectedTemplate: undefined,
    loading: false,
    contextMapping: {},
    title: 'Select WhatsApp Template',
    onRequestTemplate: undefined,
    onEditTemplate: undefined,
    providerAccount: undefined,
    initialStatus: 'ALL',
};

export default WhatsAppTemplateSelector;
